'use client'

import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  Input,
  Portal,
  Spinner,
  Textarea,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { AppField } from '@/shared'
import { useTemperatureDeferralForm } from '../model/use-temperature-deferral-form'

interface Props {
  open: boolean
  visitId: string
  donorId: string
  onClose: () => void
  /** Overrides the temperature-specific title when the modal is reused. */
  title?: string
  /** Prefills the reason, e.g. with why the card flagged the donor. */
  defaultReason?: string
}

export function TemperatureDeferralModal({
  open,
  visitId,
  donorId,
  onClose,
  title,
  defaultReason,
}: Props) {
  const { t } = useTranslation()

  const { form, isPending, onSubmit } = useTemperatureDeferralForm({
    visitId,
    donorId,
    defaultReason,
    onSuccess: onClose,
  })

  const {
    register,
    reset,
    formState: { errors },
  } = form

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) handleClose()
      }}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.surface" maxW="520px">
            <Dialog.Header pb={4}>
              <Dialog.Title fontSize="lg" fontWeight="bold">
                {title ??
                  t('donor_detail.screening.temperature_deferral.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger
                asChild
                position="absolute"
                top={4}
                right={4}
              >
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={2}>
              <form id="temperature-deferral-form" onSubmit={onSubmit}>
                <Flex direction="column" gap={4}>
                  <AppField.Root invalid={!!errors.reason}>
                    <AppField.Label fontSize="sm">
                      {t(
                        'donor_detail.screening.temperature_deferral.reason_label',
                      )}
                    </AppField.Label>
                    <Textarea
                      {...register('reason')}
                      rows={3}
                      size="sm"
                      bg="bg.subtle"
                      placeholder={t(
                        'donor_detail.screening.temperature_deferral.reason_placeholder',
                      )}
                      borderColor={errors.reason ? undefined : 'border'}
                      borderRadius="md"
                    />
                    <AppField.ErrorText>
                      {errors.reason?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.deferral_end_date}>
                    <AppField.Label fontSize="sm">
                      {t(
                        'donor_detail.screening.temperature_deferral.date_label',
                      )}
                    </AppField.Label>
                    <Input
                      {...register('deferral_end_date')}
                      type="date"
                      size="sm"
                      bg="bg.subtle"
                      borderColor={
                        errors.deferral_end_date ? undefined : 'border'
                      }
                      borderRadius="md"
                    />
                    <AppField.ErrorText>
                      {errors.deferral_end_date?.message}
                    </AppField.ErrorText>
                  </AppField.Root>
                </Flex>
              </form>
            </Dialog.Body>

            <Dialog.Footer pt={4} justifyContent="flex-end">
              <Button
                variant="outline"
                borderRadius="md"
                size="sm"
                px={6}
                onClick={handleClose}
              >
                {t('donor_detail.screening.temperature_deferral.cancel')}
              </Button>
              <Button
                type="submit"
                form="temperature-deferral-form"
                bg="primary.solid"
                color="white"
                borderRadius="md"
                size="sm"
                px={6}
                _hover={{ opacity: 0.85 }}
                _active={{ opacity: 0.7 }}
                disabled={isPending || !donorId}
              >
                {isPending ? (
                  <Spinner size="xs" color="white" />
                ) : (
                  t('donor_detail.screening.temperature_deferral.submit')
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
